import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, Loader2 } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CopyField } from '../../../components/design-system/common/CopyField';
import { ArdiChannel, useArdiApi } from '../../../common/hooks/use-ardi-api.hook';
import { readApiError } from '../../../common/utils/api-error';

interface Field {
  name: string;
  secret?: boolean;
  placeholder: string;
}

const FIELDS: Record<'telegram' | 'slack' | 'whatsapp', Field[]> = {
  telegram: [{ name: 'botToken', secret: true, placeholder: '123456789:AAF…' }],
  slack: [
    { name: 'botToken', secret: true, placeholder: 'xoxb-…' },
    { name: 'signingSecret', secret: true, placeholder: 'a1b2c3…' },
  ],
  whatsapp: [
    { name: 'phoneNumberId', placeholder: '109876543210987' },
    { name: 'accessToken', secret: true, placeholder: 'EAAG…' },
    { name: 'appSecret', secret: true, placeholder: '3f2e1d…' },
  ],
};

const LINKS: Record<'telegram' | 'slack' | 'whatsapp', string> = {
  telegram: 'https://t.me/BotFather',
  slack: 'https://api.slack.com/apps?new_app=1',
  whatsapp: 'https://developers.facebook.com/apps',
};

/** One numbered step: what to do, and anything to copy for it. */
const Step: React.FC<{ n: number; title: React.ReactNode; children?: React.ReactNode }> = ({ n, title, children }) => (
  <li className="flex gap-3">
    <span className="flex size-6 shrink-0 items-center justify-center rounded-full border text-xs tabular-nums text-muted-foreground">{n}</span>
    <div className="flex min-w-0 flex-1 flex-col gap-2 pt-0.5">
      <p className="text-sm leading-relaxed">{title}</p>
      {children}
    </div>
  </li>
);

/**
 * Connecting a chat app, step by step, in the words of that app.
 *
 * The inbound address exists before any credentials do (the channel was
 * created when the tile was picked), so Slack's manifest and Meta's webhook
 * fields can be filled with real values the vendor copies, not placeholders
 * they have to assemble.
 */
export function ChannelSetup({ channel, onConnected }: { channel: ArdiChannel; onConnected: (c: ArdiChannel) => void }) {
  const { t } = useTranslation();
  const api = useArdiApi();
  const kind = channel.kind as 'telegram' | 'slack' | 'whatsapp';
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const k = `ARDI.CHANNELS.SETUP.${kind.toUpperCase()}`;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onConnected(await api.connectChannel(channel.uuid, values));
    } catch (err) {
      const body = await readApiError(err);
      setError(body.message ? String(body.message) : (t('ARDI.CHANNELS.SETUP.FAILED') as string));
    } finally {
      setBusy(false);
    }
  };

  const inbound = channel.setup.inboundUrl ?? '';
  const httpsOk = inbound.startsWith('https://');

  return (
    <div className="flex flex-col gap-6" data-testid={`channel-setup-${kind}`}>
      {!httpsOk && kind !== 'slack' && (
        <Alert>
          <AlertDescription className="text-xs">{t('ARDI.CHANNELS.SETUP.NEEDS_HTTPS')}</AlertDescription>
        </Alert>
      )}

      <ol className="flex flex-col gap-5">
        {kind === 'telegram' && (
          <>
            <Step n={1} title={t(`${k}.STEP1`)}>
              <Button variant="outline" size="sm" asChild className="self-start">
                <a href={LINKS.telegram} target="_blank" rel="noreferrer">
                  {t(`${k}.OPEN`)}
                  <ArrowUpRight data-icon="inline-end" />
                </a>
              </Button>
            </Step>
            <Step n={2} title={t(`${k}.STEP2`)} />
            <Step n={3} title={t(`${k}.STEP3`)} />
          </>
        )}

        {kind === 'slack' && (
          <>
            <Step n={1} title={t(`${k}.STEP1`)}>
              <Button variant="outline" size="sm" asChild className="self-start">
                <a href={LINKS.slack} target="_blank" rel="noreferrer">
                  {t(`${k}.OPEN`)}
                  <ArrowUpRight data-icon="inline-end" />
                </a>
              </Button>
            </Step>
            <Step n={2} title={t(`${k}.STEP2`)}>
              {channel.setup.manifest && <CopyField value={JSON.stringify(channel.setup.manifest, null, 2)} multiline testId="slack-manifest" />}
            </Step>
            <Step n={3} title={t(`${k}.STEP3`)} />
            <Step n={4} title={t(`${k}.STEP4`)} />
          </>
        )}

        {kind === 'whatsapp' && (
          <>
            <Step n={1} title={t(`${k}.STEP1`)}>
              <Button variant="outline" size="sm" asChild className="self-start">
                <a href={LINKS.whatsapp} target="_blank" rel="noreferrer">
                  {t(`${k}.OPEN`)}
                  <ArrowUpRight data-icon="inline-end" />
                </a>
              </Button>
            </Step>
            <Step n={2} title={t(`${k}.STEP2`)}>
              <CopyField label={t(`${k}.CALLBACK_URL`)} value={inbound} />
              {channel.setup.verifyToken && <CopyField label={t(`${k}.VERIFY_TOKEN`)} value={channel.setup.verifyToken} />}
            </Step>
            <Step n={3} title={t(`${k}.STEP3`)} />
            <Step n={4} title={t(`${k}.STEP4`)} />
          </>
        )}
      </ol>

      <form onSubmit={submit} className="flex flex-col gap-4 rounded-xl border p-4">
        {FIELDS[kind].map((f) => {
          const id = `ch-${channel.uuid}-${f.name}`;
          return (
            <div key={f.name} className="flex flex-col gap-1.5">
              <Label htmlFor={id} className="text-xs font-medium text-muted-foreground">{t(`${k}.FIELD.${f.name.toUpperCase()}`)}</Label>
              <Input
                id={id}
                type={f.secret ? 'password' : 'text'}
                autoComplete="off"
                spellCheck={false}
                placeholder={f.placeholder}
                value={values[f.name] ?? ''}
                onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
                className="font-mono text-xs"
              />
            </div>
          );
        })}
        {error && <p className="text-xs text-destructive" role="alert">{error}</p>}
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">{t('ARDI.CHANNELS.SETUP.STORED_SAFELY')}</p>
          <Button type="submit" disabled={busy || FIELDS[kind].some((f) => !values[f.name]?.trim())}>
            {busy && <Loader2 className="animate-spin" />}
            {t('ARDI.CHANNELS.SETUP.CONNECT')}
          </Button>
        </div>
      </form>
    </div>
  );
}
